import React, { memo, useCallback, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { Box, Stack } from '@mui/material';
import _ from 'lodash';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';

import {
  loadCitationsBlobAction,
  loadEligibleConfigurationsAction,
  loadExecutionPlanBlobAction,
  loadMessageContentBlocksAction,
  loadPendingQuestionsAction,
  loadPendingToolConfirmationsAction,
  sendMessageAction,
  setGuidelineFullViewAction,
  setIsPanelOpenAction,
  stopChatSessionAction,
  switchConfigurationAction,
} from 'store/aiChat/actions';
import {
  selectActiveConfiguration,
  selectAiChatCitationsBlobLoaded,
  selectAiChatExecutionPlans,
  selectAiChatGuidelineContent,
  selectAiChatGuidelineFullView,
  selectAiChatHasMessages,
  selectAiChatIsAnyMessageInFlight,
  selectAiChatIsConnected,
  selectAiChatIsLoadingGuideline,
  selectAiChatIsPanelOpen,
  selectAiChatIsSending,
  selectAiChatMessageContentBlocks,
  selectAiChatMessages,
  selectAiChatPendingToolConfirmations,
  selectAiChatPendingUserQuestions,
  selectAiChatShouldShowThinkingIndicator,
  selectEligibleConfigurations,
  selectIsLoadingEligibleConfigurations,
} from 'store/aiChat/selectors';
import { getUuid, selectUserData } from 'store/auth/selectors';
import { openConfirmationDialogAction } from 'store/global/actions/confirmationDialog.actions';
import { selectApplicationIdFromDeal } from 'store/loanApplications/selectors/deal.selectors';

import { AiChatTestIds } from '../../AiChat.testIds';
import { useAssistantSlowWarning } from '../../hooks/useAssistantSlowWarning';
import { useContentScroll } from '../../hooks/useContentScroll';
import AiChatContent from '../AiChatContent';
import AiChatFooter from '../AiChatFooter';
import AiChatHeader from '../AiChatHeader';
import {
  SWITCH_CONFIGURATION_CONFIRMATION_ACCEPT,
  SWITCH_CONFIGURATION_CONFIRMATION_CANCEL,
  SWITCH_CONFIGURATION_CONFIRMATION_TITLE,
} from '../AiChatHeader/AiChatHeader.constants';
import { getSwitchConfigurationConfirmationDescription } from '../AiChatHeader/AiChatHeader.utils';
import AssistantSlowWarning from '../AssistantSlowWarning';
import { buildAskAboutGuidelinePrompt } from '../BotMessage/components/MessageContent/GuidelineExpanderCardContent/GuidelineExpanderCardContent.utils';
import GuidelineFullView from '../GuidelineFullView';

import { contentWrapperStyles, panelStyles } from './AiChatPanel.styles';

import '../../aiChat.css';

const AiChatPanel = () => {
  const dispatch = useDispatch();

  const loanId = useLoanId();
  const applicationId = useSelector(selectApplicationIdFromDeal);

  const { isScrolled, handleScroll } = useContentScroll();

  const messages = useSelector(selectAiChatMessages);
  const isSending = useSelector(selectAiChatIsSending);
  const isTyping = useSelector(selectAiChatShouldShowThinkingIndicator);
  const isConnected = useSelector(selectAiChatIsConnected);
  const isPanelOpen = useSelector(selectAiChatIsPanelOpen);
  const citationsBlobLoadedMap = useSelector(selectAiChatCitationsBlobLoaded);
  const messageContentBlocksMap = useSelector(selectAiChatMessageContentBlocks);
  const executionPlansMap = useSelector(selectAiChatExecutionPlans);
  const pendingToolConfirmations = useSelector(selectAiChatPendingToolConfirmations);
  const pendingUserQuestions = useSelector(selectAiChatPendingUserQuestions);
  const userId = useSelector(getUuid);
  const userData = useSelector(selectUserData);
  const displayName = userData?.displayName ?? '';
  const activeConfiguration = useSelector(selectActiveConfiguration);
  const eligibleConfigurations = useSelector(selectEligibleConfigurations);
  const isLoadingConfigurations = useSelector(selectIsLoadingEligibleConfigurations);
  const hasMessages = useSelector(selectAiChatHasMessages);
  const isAnyMessageInFlight = useSelector(selectAiChatIsAnyMessageInFlight);
  const guidelineFullViewCitation = useSelector(selectAiChatGuidelineFullView);
  const guidelineFullContent = useSelector(selectAiChatGuidelineContent);
  const isLoadingGuideline = useSelector(selectAiChatIsLoadingGuideline);

  const requestedCitationsRef = useRef(new Set());
  const requestedMessageContentBlocksRef = useRef(new Set());
  const requestedExecutionPlansRef = useRef(new Set());
  const requestedPendingConfirmationsRef = useRef(new Set());
  const requestedPendingQuestionsRef = useRef(new Set());

  useAssistantSlowWarning();

  useEffectSafeStrictMode(() => {
    if (!isPanelOpen || _.isNil(loanId)) {
      return;
    }

    dispatch(loadEligibleConfigurationsAction({ loanId }));
  }, [isPanelOpen, loanId, dispatch]);

  useEffectSafeStrictMode(() => {
    if (!isPanelOpen || _.isNil(loanId) || _.isEmpty(messages)) {
      return;
    }

    messages.forEach(message => {
      if (!message.hasCitations || _.isNil(message.id) || message.isStreaming) {
        return;
      }

      if (requestedCitationsRef.current.has(message.id)) {
        return;
      }

      const isBlobLoaded = citationsBlobLoadedMap[message.id];

      if (isBlobLoaded) {
        return;
      }

      requestedCitationsRef.current.add(message.id);
      dispatch(loadCitationsBlobAction({ loanId, messageId: message.id }));
    });
  }, [isPanelOpen, loanId, messages, citationsBlobLoadedMap, dispatch]);

  useEffectSafeStrictMode(() => {
    if (!isPanelOpen || _.isNil(loanId) || _.isEmpty(messages)) {
      return;
    }

    const messageIdsToLoad = messages
      .filter(
        message =>
          !_.isNil(message.id) &&
          message.hasMessageContentBlocks &&
          !requestedMessageContentBlocksRef.current.has(message.id) &&
          _.isEmpty(messageContentBlocksMap[message.id]),
      )
      .map(message => message.id);

    if (_.isEmpty(messageIdsToLoad)) {
      return;
    }

    batch(() => {
      messageIdsToLoad.forEach(messageId => {
        requestedMessageContentBlocksRef.current.add(messageId);
        dispatch(loadMessageContentBlocksAction({ loanId, messageId }));
      });
    });
  }, [isPanelOpen, loanId, messages, messageContentBlocksMap, dispatch]);

  useEffectSafeStrictMode(() => {
    if (!isPanelOpen || _.isNil(loanId) || _.isEmpty(messages)) {
      return;
    }

    const messageIdsToLoad = messages
      .filter(
        message =>
          !_.isNil(message.id) &&
          message.hasExecutionPlan &&
          !requestedExecutionPlansRef.current.has(message.id) &&
          _.isNil(executionPlansMap[message.id]),
      )
      .map(message => message.id);

    if (_.isEmpty(messageIdsToLoad)) {
      return;
    }

    batch(() => {
      messageIdsToLoad.forEach(messageId => {
        requestedExecutionPlansRef.current.add(messageId);
        dispatch(loadExecutionPlanBlobAction({ loanId, messageId }));
      });
    });
  }, [isPanelOpen, loanId, messages, executionPlansMap, dispatch]);

  useEffectSafeStrictMode(() => {
    if (!isPanelOpen || _.isNil(loanId) || _.isEmpty(messages)) {
      return;
    }

    messages.forEach(message => {
      if (_.isNil(message.id) || !message.hasPendingToolConfirmations) {
        return;
      }

      if (requestedPendingConfirmationsRef.current.has(message.id)) {
        return;
      }

      const existing = pendingToolConfirmations[message.id];

      if (!_.isEmpty(existing)) {
        return;
      }

      requestedPendingConfirmationsRef.current.add(message.id);
      dispatch(loadPendingToolConfirmationsAction({ loanId, messageId: message.id }));
    });
  }, [isPanelOpen, loanId, messages, pendingToolConfirmations, dispatch]);

  useEffectSafeStrictMode(() => {
    if (!isPanelOpen || _.isNil(loanId) || _.isEmpty(messages)) {
      return;
    }

    messages.forEach(message => {
      if (_.isNil(message.id) || !message.hasPendingQuestions) {
        return;
      }

      if (requestedPendingQuestionsRef.current.has(message.id)) {
        return;
      }

      const existing = pendingUserQuestions[message.id];

      if (!_.isEmpty(existing)) {
        return;
      }

      requestedPendingQuestionsRef.current.add(message.id);
      dispatch(loadPendingQuestionsAction({ loanId, messageId: message.id }));
    });
  }, [isPanelOpen, loanId, messages, pendingUserQuestions, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(setIsPanelOpenAction(false));
  }, [dispatch]);

  const onStop = useCallback(() => {
    if (_.isNil(loanId)) {
      return;
    }

    dispatch(stopChatSessionAction({ loanId }));
  }, [dispatch, loanId]);

  const handleSendMessage = useCallback(
    content => {
      dispatch(sendMessageAction({ loanId, applicationId, content }));
    },
    [dispatch, loanId, applicationId],
  );

  const onGuidelineFullViewClose = useCallback(() => {
    dispatch(setGuidelineFullViewAction(null));
  }, [dispatch]);

  const onAskAboutGuideline = useCallback(
    citation => {
      dispatch(
        sendMessageAction({
          loanId,
          applicationId,
          content: buildAskAboutGuidelinePrompt(citation),
        }),
      );
    },
    [dispatch, loanId, applicationId],
  );

  const onConfigurationSelect = useCallback(
    configuration => {
      if (_.isNil(configuration?.configurationId)) {
        return;
      }

      if (configuration.configurationId === activeConfiguration?.configurationId) {
        return;
      }

      const dispatchSwitch = () =>
        dispatch(
          switchConfigurationAction({
            loanId,
            configurationId: configuration.configurationId,
          }),
        );

      if (!hasMessages) {
        dispatchSwitch();

        return;
      }

      dispatch(
        openConfirmationDialogAction({
          type: ConfirmationModalType.WARNING,
          message: SWITCH_CONFIGURATION_CONFIRMATION_TITLE,
          description: getSwitchConfigurationConfirmationDescription(configuration.name),
          onAcceptButtonName: SWITCH_CONFIGURATION_CONFIRMATION_ACCEPT,
          onCancelButtonName: SWITCH_CONFIGURATION_CONFIRMATION_CANCEL,
          onAccept: dispatchSwitch,
        }),
      );
    },
    [dispatch, loanId, activeConfiguration?.configurationId, hasMessages],
  );

  return (
    <Stack data-testid={AiChatTestIds.AI_CHAT_PANEL} sx={panelStyles}>
      <AiChatHeader
        activeConfiguration={activeConfiguration}
        eligibleConfigurations={eligibleConfigurations}
        isConnected={isConnected}
        isLoadingConfigurations={isLoadingConfigurations}
        onClose={handleClose}
        onConfigurationSelect={onConfigurationSelect}
      />

      <Box data-testid={AiChatTestIds.AI_CHAT_PANEL_CONTENT} sx={contentWrapperStyles}>
        <AiChatContent
          displayName={displayName}
          isConnected={isConnected}
          isScrolled={isScrolled}
          isTyping={isTyping}
          loanId={loanId}
          messages={messages}
          userId={userId}
          onScroll={handleScroll}
        />

        {isTyping && <AssistantSlowWarning />}
        <AiChatFooter
          disabled={isSending}
          isInFlight={isAnyMessageInFlight}
          isScrolled={isScrolled}
          onSend={handleSendMessage}
          onStop={onStop}
        />
      </Box>

      <GuidelineFullView
        citation={guidelineFullViewCitation}
        fullContent={guidelineFullContent}
        isLoading={isLoadingGuideline}
        isOpen={!_.isNil(guidelineFullViewCitation)}
        onAskAboutGuideline={onAskAboutGuideline}
        onClose={onGuidelineFullViewClose}
      />
    </Stack>
  );
};

export default memo(AiChatPanel);
